import {encodeGlobalId} from '../../../forecast-app/shared/util/GlobalIdUtil';
import {FeatureFlagTeamId, getTeam, getTeamOrder, resolveTeamId} from './featureFlagTeams';

/** Company shape needed to decide eligibility — a structural subset of the Relay query result. */
export interface EligibilityCompany {
	readonly companyId: number | null;
	readonly tier: string | null;
	readonly accessBlocked: boolean | null;
}

export interface FeatureFlagGrant {
	readonly companyId: string | null;
}

/** Feature shape the list needs — a structural subset of the Relay query result. */
export interface FeatureFlagSource {
	readonly key: string | null;
	readonly name: string | null;
	readonly team: string | null;
	readonly rolledOut: boolean | null;
	readonly rolledOutDate: string | null;
	readonly cleanedUp: boolean | null;
	readonly companies: ReadonlyArray<FeatureFlagGrant | null> | null;
	readonly persons: ReadonlyArray<FeatureFlagGrant | null> | null;
}

export interface FeatureFlagRowData {
	key: string;
	name: string;
	/** null only when the backend sends a team `featureFlagTeams` does not know yet. */
	teamId: FeatureFlagTeamId | null;
	/** The mapped team's label, or the backend's raw team string when unmapped. */
	teamLabel: string;
	/** Every all-users grant, including companies outside the eligible pool. */
	companyCount: number;
	/** Individual grants, excluding users inside a company that already has an all-users grant. */
	userCount: number;
	isRolledOut: boolean;
	/** YYYY-MM-DD, or null when the backend recorded no date. */
	rolledOutDate: string | null;
	cleanedUp: boolean;
	/** 0-100. Bounded by construction: both sides of the ratio come from the eligible pool. */
	rolloutPercent: number;
	/**
	 * `rolloutPercent` as displayed. Reads `<1%` where rounding would otherwise print `0%` for a
	 * rollout that has actually reached someone, so a row cannot show a company count beside a
	 * percentage that says the flag has reached nobody.
	 */
	rolloutPercentLabel: string;
}

export const ALL_TEAMS = 'ALL';

export type TeamFilterValue = FeatureFlagTeamId | typeof ALL_TEAMS;

const isPresent = <T>(value: T | null | undefined): value is T => value != null;

/**
 * The base64 `CompanyType:{n}` id a company grant is keyed by. The single definition every
 * production and test call site routes through, so the encoding can never drift between them.
 */
export function encodeCompanyId(companyId: number | string): string {
	return encodeGlobalId('CompanyType', Number(companyId));
}

/**
 * Ids of the companies the rollout percentage is measured against: paying companies only.
 *
 * Returns a set of encoded company ids rather than a count, because the percentage has to test each
 * individual grant for membership — grants arrive already encoded that way, while the company list
 * carries raw numeric ids.
 */
export function getEligibleCompanyIds(companies: ReadonlyArray<EligibilityCompany | null> | null | undefined): Set<string> {
	const eligible = new Set<string>();
	(companies ?? []).filter(isPresent).forEach(company => {
		if (company.companyId == null || company.accessBlocked) {
			return;
		}
		if (company.tier === 'TRIAL' || company.tier === 'FREE_FOREVER') {
			return;
		}
		eligible.add(encodeCompanyId(company.companyId));
	});
	return eligible;
}

/**
 * Value to persist for a team-filter change. The design-system ComboBox emits `null` for its
 * selection when its input text is cleared — which happens naturally when backspacing out a typed
 * team name — even though the filter has no "unset" state distinct from "all teams". Collapsing the
 * two here means a cleared combobox behaves like picking "All teams" instead of persisting a raw
 * `null`, which would otherwise get stored as the string `"null"` and match no team.
 */
export function resolveTeamFilterChange(value: TeamFilterValue | null): TeamFilterValue {
	return value ?? ALL_TEAMS;
}

/**
 * Team filter to use for a raw value read back from storage. Storage only ever holds strings, so a
 * previously persisted `null` comes back as the literal string `"null"`; that value, a team name this
 * module no longer recognises, and a missing/empty value all resolve to "all teams" rather than a
 * filter that can never match a row.
 */
export function resolveStoredTeamFilter(stored: string | null | undefined): TeamFilterValue {
	if (stored === ALL_TEAMS) {
		return ALL_TEAMS;
	}
	return resolveTeamId(stored) ?? ALL_TEAMS;
}

function buildRow(feature: FeatureFlagSource, eligibleCompanyIds: Set<string>): FeatureFlagRowData {
	const companyGrants = (feature.companies ?? []).filter(isPresent);
	const grantedCompanyIds = new Set(companyGrants.map(grant => grant.companyId).filter(isPresent));

	let eligibleGranted = 0;
	grantedCompanyIds.forEach(companyId => {
		if (eligibleCompanyIds.has(companyId)) {
			eligibleGranted += 1;
		}
	});

	const userCount = (feature.persons ?? [])
		.filter(isPresent)
		.filter(person => person.companyId == null || !grantedCompanyIds.has(person.companyId)).length;

	// A team the map does not know keeps its raw backend name, so a team added to the backend before
	// it is added here still reads correctly (in grey) rather than showing a placeholder.
	const teamId = resolveTeamId(feature.team);

	const rolloutPercent = eligibleCompanyIds.size > 0 ? Math.round((eligibleGranted / eligibleCompanyIds.size) * 100) : 0;

	return {
		key: feature.key ?? '',
		name: feature.name ?? '',
		teamId,
		teamLabel: teamId ? getTeam(teamId).label : feature.team ?? '',
		companyCount: companyGrants.length,
		userCount,
		isRolledOut: feature.rolledOut === true,
		rolledOutDate: feature.rolledOutDate ? feature.rolledOutDate.substring(0, 10) : null,
		cleanedUp: feature.cleanedUp === true,
		rolloutPercent,
		rolloutPercentLabel: rolloutPercent === 0 && eligibleGranted > 0 ? '<1%' : `${rolloutPercent}%`,
	};
}

/** Row view models, sorted by team (declared order, unmapped teams last) then by flag name. */
export function buildFeatureFlagRows(
	features: ReadonlyArray<FeatureFlagSource | null> | null | undefined,
	eligibleCompanyIds: Set<string>
): FeatureFlagRowData[] {
	return (features ?? [])
		.filter(isPresent)
		.map(feature => buildRow(feature, eligibleCompanyIds))
		.sort((a, b) => {
			const byTeam = getTeamOrder(a.teamId) - getTeamOrder(b.teamId);
			return byTeam !== 0 ? byTeam : a.name.localeCompare(b.name);
		});
}

export function filterFeatureFlagRows(
	rows: readonly FeatureFlagRowData[],
	options: {teamId: TeamFilterValue; showRolledOut: boolean}
): FeatureFlagRowData[] {
	return rows.filter(
		row => (options.teamId === ALL_TEAMS || row.teamId === options.teamId) && (options.showRolledOut || !row.isRolledOut)
	);
}
